import React, { useEffect, useState } from "react";
import io from "socket.io-client";
import { API_BASE, SOCKET_URL } from "../../config/api";
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Divider,
  Button,
} from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { useNavigate } from "react-router-dom";
import { getMexicoDateTime, getMexicoNow } from "../../utils/mexicoTime";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

dayjs.locale("es");

const getReservationGroupKey = (reserva) => {
  if (reserva.comboGroupId) {
    return `combo-${reserva.comboGroupId}`;
  }

  return `booking-${reserva.id}`;
};

const groupReservations = (reservas) => {
  const grouped = new Map();

  reservas.forEach((reserva) => {
    const key = getReservationGroupKey(reserva);
    const currentGroup = grouped.get(key);

    if (currentGroup) {
      currentGroup.items.push(reserva);
      return;
    }

    grouped.set(key, {
      key,
      items: [reserva],
      date: reserva.date,
      time: reserva.time,
      status: reserva.status,
      duration: reserva.duration || reserva.service?.duration || 60,
      combo: Boolean(reserva.comboGroupId || (reserva.comboServiceIds?.length || 0) > 1),
    });
  });

  return Array.from(grouped.values());
};

const MyReservations = () => {
  const navigate = useNavigate();
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReservas = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE}/bookings/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Error al obtener reservas");

      const data = await res.json();
      setReservas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error cargando reservas:", error);
      setReservas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservas();
  }, []);

  useEffect(() => {
    const socket = io(SOCKET_URL);

    const handleBookingsUpdate = () => {
      fetchReservas();
    };

    const handleAvailabilityUpdate = () => {
      fetchReservas();
    };

    socket.on("bookings:updated", handleBookingsUpdate);
    socket.on("availability:updated", handleAvailabilityUpdate);

    return () => {
      socket.off("bookings:updated", handleBookingsUpdate);
      socket.off("availability:updated", handleAvailabilityUpdate);
      socket.disconnect();
    };
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "reservado":
      case "pendiente":
      case "pending":
      case "confirmado":
      case "confirmed":
        return "success";
      case "en_curso":
        return "info";
      case "cancelado":
      case "cancelled":
        return "error";
      case "finalizado":
        return "default";
      default:
        return "warning";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "reservado":
        return "Reservada";
      case "pendiente":
      case "pending":
        return "Reservada";
      case "confirmado":
      case "confirmed":
        return "Reservada";
      case "en_curso":
        return "En curso";
      case "cancelado":
      case "cancelled":
        return "Cancelada";
      case "finalizado":
        return "Finalizada";
      default:
        return status || "Reservada";
    }
  };

  const canReschedule = (group) => {
    if (!group?.date || !group?.time) return false;
    if (!["reservado", "pendiente", "pending", "confirmado", "confirmed"].includes(group.status)) return false;

    const nowInMexico = getMexicoNow();
    const reservationDateTime = getMexicoDateTime(group.date, group.time);

    if (!reservationDateTime.isValid()) return false;
    if (nowInMexico.isAfter(reservationDateTime)) return false;

    const hoursUntilReservation = reservationDateTime.diff(nowInMexico, "hour", true);
    return hoursUntilReservation >= 24;
  };

  const handleReschedule = (group) => {
    const primaryItem = group.items[0];

    if (!primaryItem?.serviceId || !primaryItem?.employeeId) return;

    const comboSelections = group.items
      .map((item) => `${item.serviceId}:${item.employeeId}`)
      .join(",");

    navigate(
      `/reservar/${primaryItem.serviceId}/${primaryItem.employeeId}?comboSelections=${comboSelections}&rescheduleBookingId=${primaryItem.id}&selectedDate=${group.date}&selectedTime=${group.time}&selectedDuration=${group.duration}`
    );
  };

  const groupedReservations = groupReservations(reservas);

  return (
    <>
      <Navbar />

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flexGrow: 1, mt: 10, mb: 10 }}>
          <Box sx={{ maxWidth: 700, mx: "auto", px: 2 }}>
            <Typography variant="h4" fontWeight="bold" mb={4}>
              Mis reservas
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: "#f8f4e8",
                border: "1px solid #eadfb8",
              }}
            >
              <Typography sx={{ fontSize: 14, color: "#5f5328" }}>
                <strong>Reagendar reserva:</strong> podés hacerlo hasta{" "}
                <strong>24 horas antes</strong> de la fecha y hora reservada.
              </Typography>
            </Paper>

            {loading ? (
              <Box sx={{ textAlign: "center", mt: 6 }}>
                <CircularProgress />
              </Box>
            ) : groupedReservations.length === 0 ? (
              <Typography color="text.secondary">
                Aún no tenés reservas realizadas.
              </Typography>
            ) : (
              groupedReservations.map((group) => (
                <Paper
                  key={group.key}
                  elevation={2}
                  sx={{ p: 3, mb: 3 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box>
                      <Typography variant="h6">
                        {group.combo
                          ? "Reserva combinada"
                          : group.items[0]?.service?.name || "Servicio"}
                      </Typography>
                      {group.combo && (
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                          {group.items
                            .map((item) => item.service?.name)
                            .filter(Boolean)
                            .join(" + ")}
                        </Typography>
                      )}
                    </Box>

                    {group.combo && (
                      <Chip label="Combinado" color="secondary" />
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {group.items.map((item) => (
                      <Chip
                        key={`${group.key}-${item.id}`}
                        label={`${item.employee?.name || "Profesional"}${item.service?.name ? ` - ${item.service.name}` : ""}`}
                        sx={{
                          backgroundColor: item.employee?.background_color || "#424242",
                          color: "white",
                        }}
                      />
                    ))}
                  </Box>

                  <Typography>
                    <strong>Fecha:</strong>{" "}
                    {dayjs(group.date).format("DD MMMM YYYY")}
                  </Typography>

                  <Typography>
                    <strong>Horario:</strong> {group.time}
                  </Typography>

                  <Typography>
                    <strong>Duración:</strong> {group.duration} min
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Chip
                      label={getStatusLabel(group.status)}
                      color={getStatusColor(group.status)}
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ mt: 2.5, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      disabled={!canReschedule(group)}
                      onClick={() => handleReschedule(group)}
                      sx={{
                        backgroundColor: "#000",
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: "#111",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#d9d9d9",
                          color: "#7a7a7a",
                        },
                      }}
                    >
                      Reagendar
                    </Button>
                  </Box>

                {/*   <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={deletingKey === group.key}
                      onClick={() => handleDeleteReservation(group)}
                    >
                      {deletingKey === group.key ? "Eliminando..." : "Eliminar reserva"}
                    </Button>
                  </Box> */}

                </Paper>
              ))
            )}
          </Box>
        </Box>

        <Footer />
      </Box>
    </>
  );
};

export default MyReservations;
